import { ApiPaginatedResponseBodyOfPaymentPlanResponse, PaymentPlanResponse, PaymentPlansApi } from '../../../../API';
import { useSetRecoilState } from 'recoil';
import {
  CacheManager,
  DataManagerHelpers,
  DataManagerHook,
  DataManagerHookReturnType,
  IDataManagerHookProps
} from '../../../DataManager';

export interface PlanSearchFilters {
  text?: string;
  promoGroup?: string;
  device?: string;
  country?: string;
}

export interface PlansHookReturnType extends DataManagerHookReturnType<PaymentPlanResponse, PaymentPlanResponse> {
  searchPlans: (params: {
    page: number;
    limit: number;
    filters?: PlanSearchFilters;
  }) => Promise<ApiPaginatedResponseBodyOfPaymentPlanResponse>;
  ingestPlans: () => Promise<boolean>;
}

export function PlansHook(
  params: IDataManagerHookProps<PaymentPlanResponse, PaymentPlanResponse>
): PlansHookReturnType {
  const dataManagerHook = DataManagerHook<PaymentPlanResponse, PaymentPlanResponse>(params);

  const api = params.useApiHook() as PaymentPlansApi;
  const setCache = useSetRecoilState(params.state.withDataCache);
  const { addRecordsToCache } = CacheManager<PaymentPlanResponse>(
    params.idField as keyof PaymentPlanResponse,
    setCache
  );
  const { handleApiError } = DataManagerHelpers({ name: params.name, toString: params.toString });

  /**
   * Fetches a single page of plans. Filtering is applied by the API, so the
   * caller never needs the full plan list.
   */
  const searchPlans = async (searchParams: { page: number; limit: number; filters?: PlanSearchFilters }) => {
    try {
      const response = await api.getPaginated(
        searchParams.limit,
        searchParams.page,
        searchParams.filters?.text,
        searchParams.filters?.promoGroup,
        searchParams.filters?.device,
        searchParams.filters?.country
      );
      const body = response.data.body;
      if (body.results?.length) {
        addRecordsToCache(body.results);
      }
      return body;
    } catch (err) {
      handleApiError(err, 'search_plans');
      return { results: [], meta: {} } as unknown as ApiPaginatedResponseBodyOfPaymentPlanResponse;
    }
  };

  const ingestPlans = async () => {
    try {
      await api.ingestPlans();
      // The ingest can add or change plans upstream, so drop the cached list and reload it. That
      // cache still backs PlanBadge; the grid itself renders a page fetched through searchPlans, so
      // the caller reloads that separately.
      dataManagerHook.clearCache();
      await dataManagerHook.getAll();
      return true;
    } catch (err) {
      handleApiError(err, 'ingest_plans');
      return false;
    }
  };

  return {
    ...dataManagerHook,
    searchPlans,
    ingestPlans
  };
}
